import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

function PolicyLayout({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-xl">Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span></Link>
          <Link to="/"><Button variant="ghost" size="sm" className="rounded-sm">← Home</Button></Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-20 prose prose-sm dark:prose-invert" data-testid={testId}>
        <div className="overline text-primary">/ LEGAL</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2 mb-2">{title}</h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
        <div className="mt-10 space-y-6 text-base leading-relaxed">{children}</div>
      </div>
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-xs font-mono-plex text-muted-foreground uppercase tracking-widest">
          <div>© 2026 RuleAI.Systems</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-primary">Privacy</Link>
            <Link to="/terms" className="hover:text-primary">Terms</Link>
            <Link to="/cookies" className="hover:text-primary">Cookies</Link>
            <Link to="/contact" className="hover:text-primary">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function Privacy() {
  return (
    <PolicyLayout title="Privacy Policy" subtitle="Effective February 6, 2026" testId="privacy-page">
      <p>RuleAI.Systems (&ldquo;RuleAI&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) provides an AI-powered Business Operating System. This policy explains what data we collect, how we use it, and your rights.</p>

      <h2 className="font-display text-2xl font-bold mt-8">1. Data we collect</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Account data:</strong> name, email, organization name, hashed password.</li>
        <li><strong>Workspace content:</strong> SOPs, records, employees, documents, and other data you create in RuleAI.</li>
        <li><strong>Usage data:</strong> page views, feature interactions, session identifiers.</li>
        <li><strong>Device data:</strong> IP address, browser, operating system.</li>
        <li><strong>Payment data:</strong> processed by Stripe. We never store card numbers.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold mt-8">2. How we use data</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Provide, secure, and improve the platform</li>
        <li>Deliver AI features (via Anthropic, OpenAI, and other model providers)</li>
        <li>Communicate about your account, billing, and product updates</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2 className="font-display text-2xl font-bold mt-8">3. Sharing</h2>
      <p>We do not sell your data. We share limited data with subprocessors (Stripe for billing, Anthropic/OpenAI for AI generation, MongoDB Atlas / hosting infra). Each subprocessor is bound by contractual data-protection terms.</p>

      <h2 className="font-display text-2xl font-bold mt-8">4. AI and your content</h2>
      <p>Content you send to AI features (SOPs, prompts, chat) is transmitted to Anthropic (Claude Sonnet 4.6) via our Universal AI proxy for the sole purpose of returning a response. We do not use your data to train third-party models.</p>

      <h2 className="font-display text-2xl font-bold mt-8">5. Your rights</h2>
      <p>You may request access, correction, export, or deletion of your personal data by emailing privacy@ruleai.systems. EU/UK residents have rights under GDPR; California residents under CCPA/CPRA.</p>

      <h2 className="font-display text-2xl font-bold mt-8">6. Data retention</h2>
      <p>We retain workspace data as long as your account is active. On account deletion, data is removed within 30 days, subject to legal holds.</p>

      <h2 className="font-display text-2xl font-bold mt-8">7. Security</h2>
      <p>Data is encrypted in transit (TLS 1.2+). Passwords are hashed with bcrypt. Access is scoped by organization (multi-tenant isolation) and by role (RBAC).</p>

      <h2 className="font-display text-2xl font-bold mt-8">8. Contact</h2>
      <p>Questions about this policy: <a href="mailto:privacy@ruleai.systems" className="text-primary hover:underline">privacy@ruleai.systems</a></p>
    </PolicyLayout>
  );
}

export function Terms() {
  return (
    <PolicyLayout title="Terms of Service" subtitle="Effective February 6, 2026" testId="terms-page">
      <p>By using RuleAI.Systems (&ldquo;the Service&rdquo;), you agree to these Terms.</p>

      <h2 className="font-display text-2xl font-bold mt-8">1. Accounts</h2>
      <p>You must be 18+ and authorized to enter into contracts. You are responsible for your account credentials and for all activity in your organization workspace.</p>

      <h2 className="font-display text-2xl font-bold mt-8">2. Subscription &amp; billing</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Business plans are billed monthly or annually via Stripe.</li>
        <li>A 14-day free trial is provided; you may cancel before renewal.</li>
        <li>Fees are non-refundable except where required by law.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold mt-8">3. Acceptable use</h2>
      <p>You may not: violate laws, upload malware, attempt to reverse-engineer the Service, harass others, or use the Service to build a competing product.</p>

      <h2 className="font-display text-2xl font-bold mt-8">4. Your content</h2>
      <p>You retain all rights to your content. You grant us a limited license to store, process, and display it as needed to provide the Service.</p>

      <h2 className="font-display text-2xl font-bold mt-8">5. AI outputs</h2>
      <p>AI-generated content is provided as-is. You are responsible for reviewing and validating outputs before relying on them for business decisions. RuleAI is not liable for errors in AI-generated content.</p>

      <h2 className="font-display text-2xl font-bold mt-8">6. Termination</h2>
      <p>Either party may terminate for material breach. We may suspend accounts for violations of these Terms. You may cancel your subscription anytime from the Billing page.</p>

      <h2 className="font-display text-2xl font-bold mt-8">7. Warranty disclaimer</h2>
      <p>THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL IMPLIED WARRANTIES INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.</p>

      <h2 className="font-display text-2xl font-bold mt-8">8. Limitation of liability</h2>
      <p>OUR TOTAL LIABILITY IS LIMITED TO FEES PAID IN THE PRIOR 12 MONTHS. IN NO EVENT ARE WE LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.</p>

      <h2 className="font-display text-2xl font-bold mt-8">9. Contact</h2>
      <p><a href="mailto:legal@ruleai.systems" className="text-primary hover:underline">legal@ruleai.systems</a></p>
    </PolicyLayout>
  );
}

export function Cookies() {
  return (
    <PolicyLayout title="Cookie Policy" subtitle="Effective February 6, 2026" testId="cookies-page">
      <h2 className="font-display text-2xl font-bold">What we use</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Essential:</strong> authentication tokens (JWT) stored in localStorage; session identifiers for analytics.</li>
        <li><strong>Analytics:</strong> anonymized page-view tracking to understand what content resonates. No third-party trackers.</li>
        <li><strong>Preferences:</strong> theme (light/dark), dismissed banners.</li>
      </ul>
      <h2 className="font-display text-2xl font-bold mt-8">Managing cookies</h2>
      <p>You can clear cookies and localStorage from your browser settings. Doing so will log you out and reset preferences.</p>
      <h2 className="font-display text-2xl font-bold mt-8">Third-party services</h2>
      <p>Stripe sets cookies for payment processing when you visit the checkout. See Stripe&rsquo;s cookie policy for details.</p>
    </PolicyLayout>
  );
}
